/* eslint-disable prettier/prettier */
import { CollapsibleSection } from '../CollapsibleSection';
import { KeyValuePair } from '../../ui/KeyValuePair';
import { getFieldValue } from '../../../utils/getFieldValue';
import { emojiIconMap } from '../../../utils/emojiIconMap';
import { TreatyEntityType } from '../../../types/TreatyEntityType';

export const LayerRenderer = ({
  layer,
  index,
  fieldConfigMap = {},
  refreshTreatyData,
}: {
  layer: any;
  index: number;
  fieldConfigMap?: Record<string, any>;
  refreshTreatyData?: () => void;
}) => {
  const layerNameField = getFieldValue(layer.layerName);

  return (
    <CollapsibleSection
      title={`Layer ${index}: ${layerNameField.value}`}
      icon={<span>{emojiIconMap['Layers']}</span>}
    >
      {[
        'limit',
        'deductible',
        'premiumRate',
        'depositPremium',
        'minimumPremium',
        'layerCurrency',
      ].map((key) => {
        const { value, ai_result } = getFieldValue(layer[key]);
        return (
          <KeyValuePair
            key={key}
            label={formatKey(key)}
            value={value}
            aiValue={ai_result}
            entityId={layer?.id}
            entityType={TreatyEntityType.LAYER}
            fieldKey={key}
            editable
            refreshTreatyData={refreshTreatyData}
            fieldConfig={fieldConfigMap[key] || {}}
          />
        );
      })}

      {renderSection('Installments', layer.installments, refreshTreatyData)}
      {renderSection('Reinstatements', layer.reinstatments, refreshTreatyData)}
      {renderSection('Adjustments', layer.adjustments, refreshTreatyData)}

      {layer.subLayers?.length > 0 && (
        <>
          {layer.subLayers.length === 1 ? (
            <LayerRenderer
              layer={layer.subLayers[0]}
              index={1}
              fieldConfigMap={fieldConfigMap}
              refreshTreatyData={refreshTreatyData}
            />
          ) : (
            <CollapsibleSection title="Sublayers" icon={<span>{emojiIconMap['Sublayers']}</span>}>
              {layer.subLayers.map((sub: any, i: number) => (
                <LayerRenderer
                  key={i}
                  layer={sub}
                  index={i + 1}
                  fieldConfigMap={fieldConfigMap}
                  refreshTreatyData={refreshTreatyData}
                />
              ))}
            </CollapsibleSection>
          )}
        </>
      )}
    </CollapsibleSection>
  );
};

const formatKey = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

const renderSection = (
  title: string,
  items?: any[],
  refreshTreatyData?: () => void,
) => {
  if (!items || items.length === 0) return null;

  const icon = emojiIconMap[title];

  const getEntityType = (title: string) => {
    switch (title) {
      case 'Installments':
        return TreatyEntityType.INSTALLMENT;
      case 'Reinstatements':
        return TreatyEntityType.REINSTATEMENT;
      case 'Adjustments':
        return TreatyEntityType.ADJUSTMENT;
      default:
        return undefined;
    }
  };

  const entityType = getEntityType(title);

  if (items.length === 1) {
    const item = items[0];
    return (
      <div className="mt-3">
        <CollapsibleSection title={title} icon={<span>{icon}</span>}>
          {Object.entries(item)
            .filter(([k]) => k !== 'id')
            .map(([k, v]) => {
              const { value, ai_result } = getFieldValue(v);
              return (
                <KeyValuePair
                  key={k}
                  label={formatKey(k)}
                  value={value}
                  aiValue={ai_result}
                  entityId={item.id}
                  entityType={entityType}
                  fieldKey={k}
                  editable
                  refreshTreatyData={refreshTreatyData}
                />
              );
            })}
        </CollapsibleSection>
      </div>
    );
  }

  return (
    <CollapsibleSection title={title} icon={<span>{icon}</span>}>
      {items.map((item, i) => (
        <CollapsibleSection key={i} title={`${title.slice(0, -1)} ${i + 1}`}>
          {Object.entries(item)
            .filter(([k]) => k !== 'id')
            .map(([k, v]) => {
              const { value, ai_result } = getFieldValue(v);
              return (
                <KeyValuePair
                  key={k}
                  label={formatKey(k)}
                  value={value}
                  aiValue={ai_result}
                  entityId={item.id}
                  entityType={entityType}
                  fieldKey={k}
                  editable
                  refreshTreatyData={refreshTreatyData}
                />
              );
            })}
        </CollapsibleSection>
      ))}
    </CollapsibleSection>
  );
};
